import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Button, Chip, IconButton, Stack, Typography } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import dayjs from "dayjs";

import { ISubscriptionItem } from "@/api/types/subscription";
import headCells from "./headCells";
import TableWrapperStyle from "./style";
import { SubscriptionHistoryData } from "./type";
import ROUTE_CONSTANTS from "@/constants/route-constants";

import { getComparator } from "@/utils/table-utils";
import { Order } from "@/utils/table-utils";
import EnhancedTableHead from "@/components/app/EnhancedTableHeader";
import TableSkeleton from "@/components/app/TableSkeleton";
import Loading from "@/components/app/Loading/tableLoading";

export default function SubscriptionHistoryTable({
    data: rows,
    totalItems,
    rowsPerPage,
    page,
    onChange,
    isFetching,
    isLoading,
}: {
    data: SubscriptionHistoryData[],
    totalItems: number,
    page: number,
    rowsPerPage: number,
    isFetching: boolean,
    isLoading: boolean,
    onChange: (data: { page?: number, rowsPerPage?: number }) => void,
}) {
    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof SubscriptionHistoryData>("createdAt");
    const navigate = useNavigate();
    const { id: customerId } = useParams<{ id: string }>();

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof SubscriptionHistoryData) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        onChange({ page: newPage, rowsPerPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
    };

    const handleViewTransactions = (subscription: SubscriptionHistoryData) => {
        navigate(`/user-management/customers/${customerId}/subscription/${subscription.id}`);
    };

    const getStatusColor = (status: string) => {
        switch (status.toUpperCase()) {
            case "ACTIVE":
                return "success";
            case "EXPIRED":
                return "error";
            case "CANCELLED":
                return "warning";
            case "PENDING":
                return "info";
            default:
                return "default";
        }
    };

    const getPaymentStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case "success":
                return "success";
            case "pending":
                return "warning";
            case "failed":
                return "error";
            default:
                return "default";
        }
    };

    const visibleRows = useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows]);

    return (
        <>
            <TableWrapperStyle className="table-style-wrapper" sx={{ width: "100%", marginTop: "2em" }}>
                <TableContainer sx={{ maxHeight: "700px" }}>
                    <Table
                        stickyHeader
                        aria-labelledby="tableTitle"
                        className="table"
                        component="div"
                        size="medium"
                        sx={{ padding: ".5em" }}
                    >
                        <EnhancedTableHead<SubscriptionHistoryData>
                            hasAction={true}
                            headCells={headCells}
                            order={order}
                            orderBy={orderBy}
                            rowCount={rows.length}
                            onRequestSort={handleRequestSort}
                        />
                        <TableBody component="div">
                            {isLoading && <TableSkeleton cells={headCells.map((item) => String(item.id))} />}
                            {!isLoading && !isFetching && visibleRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={headCells.length}>No Subscription History To Show</TableCell>
                                </TableRow>
                            )}
                            {isFetching && <Loading />}
                            {visibleRows.map((row) => {
                                return (
                                    <TableRow
                                        key={row.id}
                                        className="table-row"
                                        component="div"
                                        role="checkbox"
                                        tabIndex={-1}
                                    >
                                        <TableCell align="left" className="table-cell" component="div">
                                            <span className="table-value">{row.planName}</span>
                                        </TableCell>
                                        <TableCell align="left" className="table-cell" component="div">
                                            <span className="table-value fit">
                                                <Chip
                                                    color={getStatusColor(row.status)}
                                                    label={row.status}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </span>
                                        </TableCell>
                                        <TableCell align="left" className="table-cell" component="div">
                                            <span className="table-value">{dayjs(row.startDate).format("YYYY-MM-DD")}</span>
                                        </TableCell>
                                        <TableCell align="left" className="table-cell" component="div">
                                            <span className="table-value">{dayjs(row.endDate).format("YYYY-MM-DD")}</span>
                                        </TableCell>
                                        <TableCell align="left" className="table-cell" component="div">
                                            <span className="table-value fit">
                                                <Chip
                                                    color={getPaymentStatusColor(row.paymentStatus)}
                                                    label={row.paymentStatus}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </span>
                                        </TableCell>
                                        <TableCell align="left" className="table-cell" component="div">
                                            <span className="table-value">
                                                {dayjs(row.paymentDate).format("YYYY-MM-DD")}
                                            </span>
                                        </TableCell>
                                        <TableCell align="left" className="table-cell" component="div">
                                            <span className="table-value">{row.source}</span>
                                        </TableCell>
                                        <TableCell align="right" className="table-cell" component="div">
                                            <span
                                                className="table-value"
                                                style={{ padding: "5px", justifyContent: "flex-end" }}
                                            >
                                                <IconButton onClick={() => handleViewTransactions(row)}>
                                                    <ArrowForward />
                                                </IconButton>
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalItems}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableWrapperStyle>
        </>
    );
}
